/* eslint-disable indent */
import {
  Box,
  Divider,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import InputAdornmentChip from 'components/InputAdornmentChip';
import Permission from 'components/ValidPermission';
import { Formik } from 'formik';
import i18n from 'i18n';
import Button from 'components/Button';
import {
  EnvironmentFields,
  FormKeepingUserConnectedProps,
  ServerFields,
} from 'modules/environments/interfaces';
import { publishNewImageRequest } from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { useParams } from 'react-router';
import { ModalFields } from 'modules/modal/interfaces';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ImagesLogs from 'containers/Environments/ListServers/PreparationServer/ImagesLogs';
import PageTitle from 'components/PageTitle';
import styles from './styles';
import schema from './schema';

const useStyles = makeStyles(styles);

interface Props {
  environmentSelected: EnvironmentFields | null;
  preparationServer: ServerFields;
  highlightSection?: string;
  publishNewImageRequest: ({
    environmentUuid,
    instanceId,
    values,
    type,
  }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
}

const KeepPublication = ({
  environmentSelected,
  preparationServer,
  highlightSection,
  publishNewImageRequest,
  modalOpen,
  modalClose,
  setCurrentTab,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();

  const sectionRef = useRef<HTMLDivElement>(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  if (highlightSection === 'logs') {
    scrollToSection();
  }

  const initialValues: FormKeepingUserConnectedProps = {
    operator_name: '',
    notification_email: [],
  };

  const handleConfirmPublish = values => {
    modalOpen({
      title: i18n.t('KEEP_PUBLICATION_KEY'),
      maxWidth: 'sm',
      footer: (
        <Grid>
          <Button
            id="submit-button"
            submitButton
            onClick={() => {
              publishNewImageRequest({
                environmentUuid: id,
                instanceId: preparationServer?.instance_id,
                values,
                type: 'keeping_user_sessions',
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
      content: (
        <Typography className={classes.headerText}>
          {i18n.t('KEEP_PUBLICATION_CONFIRM_KEY')}
        </Typography>
      ),
    });
  };

  return (
    <>
      <Grid container className={classes.formGrid}>
        <Grid container>
          <Grid
            container
            justify="center"
            spacing={2}
            className={classes.executedPublicationsModal}
          >
            <Grid item xs={6} className={classes.executedPublications}>
              <TextField
                id="executed-instance-blocking-publications"
                label={i18n.t(
                  'ENVIRONMENTS_PAGE.EXECUTED_MONTHLY_PUBLICATION_KEY'
                )}
                value={
                  environmentSelected &&
                  environmentSelected?.publication
                    ?.limit_instance_blocking_publications -
                    environmentSelected?.publication
                      ?.executed_instance_blocking_publications
                }
                variant="outlined"
                type="number"
                InputProps={{ readOnly: true }}
                disabled={true}
                helperText={
                  <Typography component="div" className={classes.limitText}>
                    {`${i18n.t('MAX_PUBLICATIONS_KEY')}: `}
                    <Box
                      fontWeight="bold"
                      display="inline"
                      className={classes.limitTextBold}
                    >
                      {
                        environmentSelected?.publication
                          ?.limit_instance_blocking_publications
                      }
                    </Box>
                  </Typography>
                }
              />
            </Grid>
            {environmentSelected?.publication
              ?.limit_instance_blocking_publications_daily !== null && (
              <Grid item xs={6} className={classes.executedPublications}>
                <TextField
                  id="executed-instance-blocking-publications-daily"
                  label={i18n.t(
                    'ENVIRONMENTS_PAGE.EXECUTED_DAILY_PUBLICATION_KEY'
                  )}
                  value={
                    environmentSelected &&
                    Number(
                      environmentSelected?.publication
                        ?.limit_instance_blocking_publications_daily
                    ) -
                      environmentSelected?.publication
                        ?.executed_instance_blocking_publications_daily
                  }
                  variant="outlined"
                  type="number"
                  InputProps={{ readOnly: true }}
                  disabled={true}
                  helperText={
                    <Typography component="div" className={classes.limitText}>
                      {`${i18n.t('MAX_DAILY_PUBLICATIONS_KEY')}: `}
                      <Box
                        fontWeight="bold"
                        display="inline"
                        className={classes.limitTextBold}
                      >
                        {
                          environmentSelected?.publication
                            ?.limit_instance_blocking_publications_daily
                        }
                      </Box>
                    </Typography>
                  }
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container className={classes.header}>
          <Typography className={classes.headerText}>
            {i18n.t('ENVIRONMENTS_PAGE.KEEP_USER_LOG_MSG_KEY')}
          </Typography>
        </Grid>
        <Grid container>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleConfirmPublish}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid container spacing={2}>
                <InputAdornmentChip
                  gridXs={12}
                  label="Email"
                  stateKey="notification_email"
                  values={environmentSelected?.trusted_users?.split(',') || []}
                  setFieldValue={setFieldValue}
                />
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('ENVIRONMENTS_PAGE.YOUR_NAME_KEY'),
                      stateKey: 'operator_name',
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <Grid container justify="flex-end" className={classes.buttons}>
                  <Button id="submit-button" onClick={submitForm} submitButton>
                    {i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      </Grid>
      <Grid container>
        <Divider className={classes.divider} />
      </Grid>
      <Grid container>
        <Grid container className={classes.actionsGrid}>
          <Grid item>
            <PageTitle
              title={i18n.t('ENVIRONMENTS_PAGE.LOGS_PUBLICATION_KEY')}
              subtitle={i18n.t('LOGS_PUBLICATION_SUBSCRIPTION_KEY')}
            />
          </Grid>
          <Grid item>
            <Permission value="list_models">
              <Button
                className={classes.seeModelsButtons}
                variant="outlined"
                onClick={() => setCurrentTab(2)}
              >
                {i18n.t('SEE_GENERATED_MODELS_KEY')}
              </Button>
            </Permission>
          </Grid>
        </Grid>
        <Grid container innerRef={sectionRef}>
          <ImagesLogs id={id} instanceId={preparationServer?.instance_id} />
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected },
}: ApplicationState) => ({
  environmentSelected,
});

export default connect(mapStateToProps, {
  publishNewImageRequest,
  modalOpen,
  modalClose,
})(KeepPublication);
